import { Lock } from '@mui/icons-material'
import { Box, Button, Tooltip } from '@mui/material'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { ReactElement, useContext, useMemo, useState } from 'react'
import { valueof } from 'src/@types/global'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { FinalProgramReportExportMenu } from 'src/screens/kap/shared/FinalProgramReportExportMenu'
import { SummarySection } from 'src/screens/shared/application/common/SummarySection'
import { ValidationListItemProps } from 'src/screens/shared/common/SummaryValidationUtils'
import { useMilestoneTitle } from 'src/screens/shared/implementation/details/milestone/MilestoneTitleContext'
import { getMilestoneSummaryTitleLabel } from 'src/screens/shared/implementation/details/milestone/utils/MilestoneUtils'
import { DossierService } from 'src/service/axios/DossierService'
import { MilestoneWorkflowService } from 'src/service/axios/MilestoneWorkflowService'
import { EditButton } from 'src/shared/button/Buttons'
import { MilestoneFinalReportChart } from 'src/shared/charts/milestone-final-report/MilestoneFinalReportChart'
import { PROCESS, PROJECT, PROJECT_TYPE, PROJECT_USER_ROLE_TYPE } from 'src/shared/constants/constants'
import { MILESTONE_STATUS, MILESTONE_TYPE, MILESTONE_TYPE_TYPE } from 'src/shared/constants/milestone-constants'
import { NextState } from 'src/shared/constants/workflow-constants'
import { DeleteIcon } from 'src/shared/icons/Icons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { FinalProjectReportExportMenu } from 'src/shared/menu/FinalProjectReportExportMenu'
import { KapMilestoneExportMenu } from 'src/shared/menu/KapMilestoneExportMenu'
import { ProjectMilestoneExportMenu } from 'src/shared/menu/ProjectMilestoneExportMenu'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { ReadOnlySelection } from 'src/shared/presentation/ReadOnly'
import { Section } from 'src/shared/presentation/Section'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { NextStateIf } from 'src/shared/workflow/NextStateIf'
import { UserContext } from 'src/user/UserContext'

interface Props {
  loading: boolean
  milestoneSummary: any | undefined
  milestoneType: MILESTONE_TYPE_TYPE
  processType: PROJECT_TYPE
  canEdit: boolean
  canDeleteMilestone: boolean
  canEditDueDate: boolean
  onBack: () => void
  onEditComment: () => void
  onEditDueDate?: () => void
  validationViolations: ValidationListItemProps[]
  handleViolationClick: (item: ValidationListItemProps) => void
  userProjectRoles?: Array<valueof<PROJECT_USER_ROLE_TYPE>>
  nextStates: NextState[] | null
  refetchPageData: () => void
}

export const MilestoneSummaryView = ({
  loading,
  milestoneSummary,
  milestoneType,
  processType,
  canEdit,
  canDeleteMilestone,
  canEditDueDate,
  onBack,
  onEditComment,
  onEditDueDate,
  validationViolations,
  handleViolationClick,
  userProjectRoles,
  nextStates,
  refetchPageData,
}: Props): ReactElement => {
  const { user } = useContext(UserContext)
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const title = useMilestoneTitle()

  const [doneConfirmationOpen, setDoneConfirmationOpen] = useState(false)
  const [reviewConfirmationOpen, setReviewConfirmationOpen] = useState(false)
  const [reworkConfirmationOpen, setReworkConfirmationOpen] = useState(false)
  const [deleteMilestoneConfirmationOpen, setDeleteMilestoneConfirmationOpen] = useState(false)
  const [deleteMilestoneLoading, setDeleteMilestoneLoading] = useState(false)
  const [chartBase64Image, setChartBase64Image] = useState<string>()

  const userGlobalRoles = user.roles

  const confirmSetToDoneHandler = async () => {
    try {
      await MilestoneWorkflowService.transition(milestoneSummary?.workflow_id as string, MILESTONE_STATUS.DONE)
      refetchPageData()
      setDoneConfirmationOpen(false)
      notificationService.changesSaved()
    } catch (e) {
      notificationService.operationFailed()
    }
  }

  const confirmSetToReviewHandler = async () => {
    try {
      await MilestoneWorkflowService.transition(milestoneSummary?.workflow_id as string, MILESTONE_STATUS.REVIEW)
      refetchPageData()
      setReviewConfirmationOpen(false)
      notificationService.changesSaved()
    } catch (e) {
      notificationService.operationFailed()
    }
  }

  const confirmSetToReworkHandler = async () => {
    try {
      await MilestoneWorkflowService.transition(milestoneSummary?.workflow_id as string, MILESTONE_STATUS.REWORK)
      refetchPageData()
      setReworkConfirmationOpen(false)
      notificationService.changesSaved()
    } catch (e) {
      notificationService.operationFailed()
    }
  }

  const openDeleteMilestoneConfirmationModalDialog = () => {
    setDeleteMilestoneConfirmationOpen(true)
  }

  const onCancelDeleteMilestone = () => {
    setDeleteMilestoneConfirmationOpen(false)
  }

  const onConfirmDeleteMilestone = () => {
    setDeleteMilestoneLoading(true)

    DossierService.deleteMilestone(milestoneSummary.id)
      .then(() => {
        notificationService.deleteSuccessful()
        onBack()
      })
      .catch(() => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setDeleteMilestoneLoading(false)
      })
  }

  const summaryTitle = useMemo(() => {
    const milestoneSummaryTitleLabel = getMilestoneSummaryTitleLabel(processType, milestoneType)
    return `${getMessage(milestoneSummaryTitleLabel, [milestoneSummary?.year_in_focus ?? ''])}`
  }, [getMessage, milestoneSummary, milestoneType, processType])

  return (
    <ScreenLayout
      title={title}
      onBack={onBack}
      actions={
        <>
          {processType === PROJECT.KAP
            ? Utils.canExportKapMilestone(milestoneType) && (
                <KapMilestoneExportMenu year={milestoneSummary?.year_in_focus} />
              )
            : Utils.canExportMilestone(milestoneType) && (
                <ProjectMilestoneExportMenu
                  process={processType.toLocaleUpperCase() as PROJECT_TYPE}
                  year={milestoneSummary?.year_in_focus}
                />
              )}
          {processType === PROCESS.KAP && milestoneType === MILESTONE_TYPE.FINAL_REPORT && (
            <FinalProgramReportExportMenu />
          )}

          {(processType === PROCESS.PF_KAP || processType === PROCESS.PF_PGV) &&
            milestoneType === MILESTONE_TYPE.FINAL_REPORT && (
              <FinalProjectReportExportMenu
                process={processType.toUpperCase() as PROJECT_TYPE}
                chartBase64Image={chartBase64Image}
              />
            )}
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && milestoneSummary && nextStates !== null && (
            <>
              <SummarySection
                title={summaryTitle}
                actionsHelpLabel={`label.help.milestone.summary.${processType.toLowerCase()}.${milestoneType}`}
                status={
                  <span>
                    {getMessage('label.status')}: {getMessage(`label.milestone.status.${milestoneSummary.status}`)}
                  </span>
                }
                violations={validationViolations}
                violationClickHandler={handleViolationClick}
                actionsCompleteMessage={getMessage(`label.milestone.summary.check.complete.${milestoneType}`, [
                  milestoneSummary.year_in_focus ?? '',
                ])}
                statusActions={
                  <>
                    <NextStateIf
                      forState={MILESTONE_STATUS.REVIEW}
                      nextStates={nextStates}
                      userGlobalRoles={userGlobalRoles}
                      userProjectRoles={userProjectRoles}
                      canEditOwnCanton={canEdit}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={validationViolations.length > 0}
                        onClick={() => setReviewConfirmationOpen(true)}
                      >
                        {getMessage('button.set.to.review')}
                      </Button>
                    </NextStateIf>
                    <NextStateIf
                      forState={MILESTONE_STATUS.REWORK}
                      nextStates={nextStates}
                      userGlobalRoles={userGlobalRoles}
                      userProjectRoles={userProjectRoles}
                      canEditOwnCanton={canEdit}
                    >
                      <Button variant="outlined" color="secondary" onClick={() => setReworkConfirmationOpen(true)}>
                        {getMessage('button.set.to.rework')}
                      </Button>
                    </NextStateIf>
                    <NextStateIf
                      forState={MILESTONE_STATUS.DONE}
                      nextStates={nextStates}
                      userGlobalRoles={userGlobalRoles}
                      userProjectRoles={userProjectRoles}
                      canEditOwnCanton={canEdit}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={validationViolations.length > 0}
                        onClick={() => setDoneConfirmationOpen(true)}
                      >
                        {getMessage('button.set.to.done')}
                      </Button>
                    </NextStateIf>
                  </>
                }
                commentsHelpLabel={`label.help.milestone.summary.comments.${processType.toLowerCase()}.${milestoneType}`}
                commentActions={<EditButton onClick={onEditComment} hidden={!canEdit} />}
                comment={milestoneSummary.comment}
                sectionActionButton={
                  <>
                    {canDeleteMilestone && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={openDeleteMilestoneConfirmationModalDialog}
                        startIcon={<DeleteIcon />}
                      >
                        {getMessage('button.milestone.delete')}
                      </Button>
                    )}
                  </>
                }
                sectionBeforeComment={
                  Utils.isInternalRole(userGlobalRoles) && (
                    <Section
                      id="milestone-due-date"
                      title={getMessage('label.title.milestone.due-date')}
                      titleEndIcon={
                        <Tooltip title={getMessage('label.navigation.gfch.only.tooltip')} placement="right-end">
                          <Lock color="inherit" fontSize="inherit" />
                        </Tooltip>
                      }
                      actionButton={
                        <EditButton origin="content" color="primary" onClick={onEditDueDate} hidden={!canEditDueDate} />
                      }
                      helpAndInstructions={
                        <HelpAndInstructions labelKey="label.help.milestone.summary.edit.due-date" />
                      }
                    >
                      <Card>
                        <CardContent>
                          <Box>
                            <ReadOnlySelection text={getMessage('label.due.date')}>
                              {milestoneSummary?.due_date && DateUtils.parseAndFormatDate(milestoneSummary.due_date)}
                            </ReadOnlySelection>
                          </Box>
                        </CardContent>
                      </Card>
                    </Section>
                  )
                }
              />

              <SecondaryConfirmationModalDialog
                open={doneConfirmationOpen}
                onCancel={() => setDoneConfirmationOpen(false)}
                onConfirm={confirmSetToDoneHandler}
                titleKey="label.milestone.set.to.done.confirm.title"
                confirmButtonKey="button.set.to.done"
              >
                {getMessage('label.milestone.set.to.done.confirm.body')}
              </SecondaryConfirmationModalDialog>
              <SecondaryConfirmationModalDialog
                open={reviewConfirmationOpen}
                onCancel={() => setReviewConfirmationOpen(false)}
                onConfirm={confirmSetToReviewHandler}
                titleKey="label.milestone.set.to.review.confirm.title"
                confirmButtonKey="button.set.to.review"
              >
                {getMessage('label.milestone.set.to.review.confirm.body')}
              </SecondaryConfirmationModalDialog>
              <SecondaryConfirmationModalDialog
                open={reworkConfirmationOpen}
                onCancel={() => setReworkConfirmationOpen(false)}
                onConfirm={confirmSetToReworkHandler}
                titleKey="label.milestone.set.to.rework.confirm.title"
                confirmButtonKey="button.set.to.rework"
              >
                {getMessage('label.milestone.set.to.rework.confirm.body')}
              </SecondaryConfirmationModalDialog>
              <SecondaryConfirmationModalDialog
                open={deleteMilestoneConfirmationOpen}
                onCancel={onCancelDeleteMilestone}
                onConfirm={onConfirmDeleteMilestone}
                titleKey={'label.delete.confirm.milestone.title'}
                confirmButtonKey={'button.milestone.delete'}
                buttonColor={'error'}
                loading={deleteMilestoneLoading}
              >
                {getMessage('label.delete.confirm.milestone')}
              </SecondaryConfirmationModalDialog>
            </>
          )}

          {/* Allow export document with image for Final Report */}
          {(processType === PROCESS.PF_KAP || processType === PROCESS.PF_PGV) &&
            milestoneType === MILESTONE_TYPE.FINAL_REPORT && (
              <MilestoneFinalReportChart
                setChartBase64Image={setChartBase64Image}
                style={{ visibility: 'hidden', display: 'none' }}
              />
            )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
